import { Stack, Typography } from '@mui/material';
import { PeriodTypeEnum } from '@schooly/api';
import { buildTestId, TIMETABLE_STRUCTURE } from '@schooly/constants/test-ids';
import { RadioGroupCard } from '@schooly/style';
import { useIntl } from 'react-intl';

const periodTypeTestId = (slug: string) =>
  buildTestId(TIMETABLE_STRUCTURE.PERIOD_TYPE_PREFIX, { slug }, 'slug');

export interface PeriodTypeSelection {
  type: PeriodTypeEnum;
  isRegister: boolean;
}

interface TimetableStructurePeriodTypeSelectorProps {
  type: PeriodTypeEnum;
  isRegister: boolean;
  registrationDisabled: boolean;
  onChange: (next: PeriodTypeSelection) => void;
}

export const TimetableStructurePeriodTypeSelector = ({
  type,
  isRegister,
  registrationDisabled,
  onChange,
}: TimetableStructurePeriodTypeSelectorProps) => {
  const { formatMessage } = useIntl();

  const isLesson = type === PeriodTypeEnum.Lesson;
  const isRegistrationType = type === PeriodTypeEnum.Registration;
  const limitTooltip = formatMessage({
    id: 'school-timetableStructure-registration-limit-tooltip',
  });

  // A register option is locked only when the cap is reached AND this period
  // is not already the register slot in question.
  const nestedLocked = registrationDisabled && !(isLesson && isRegister);
  const tutorLocked = registrationDisabled && !isRegistrationType;

  const description = (id: string, active: boolean) => (
    <Typography color={active ? 'primary.main' : 'text.secondary'}>
      {formatMessage({ id })}
    </Typography>
  );

  return (
    <Stack gap={1}>
      <RadioGroupCard
        dataTestId={periodTypeTestId('lesson')}
        labelTextId="school-timetableStructure-periodType-Lesson"
        checked={isLesson}
        onChange={() =>
          onChange({ type: PeriodTypeEnum.Lesson, isRegister: isLesson ? isRegister : false })
        }
      >
        {description('school-timetableStructure-periodType-Lesson-Description', isLesson)}
        {isLesson && (
          <RadioGroupCard
            dataTestId={periodTypeTestId('lesson-registration')}
            sx={{ mt: 1 }}
            labelTextId="school-timetableStructure-periodType-Lesson-Registration"
            checked={isRegister}
            controlVariant="checkbox"
            disabled={nestedLocked}
            controlTooltipContent={nestedLocked ? limitTooltip : undefined}
            onChange={() => onChange({ type: PeriodTypeEnum.Lesson, isRegister: !isRegister })}
          >
            {description(
              'school-timetableStructure-periodType-Lesson-Registration-Description',
              isRegister,
            )}
          </RadioGroupCard>
        )}
      </RadioGroupCard>

      <RadioGroupCard
        dataTestId={periodTypeTestId('registration')}
        labelTextId="school-timetableStructure-periodType-Registration"
        checked={isRegistrationType}
        disabled={tutorLocked}
        controlTooltipContent={tutorLocked ? limitTooltip : undefined}
        onChange={() => onChange({ type: PeriodTypeEnum.Registration, isRegister: true })}
      >
        {description(
          'school-timetableStructure-periodType-Registration-Description',
          isRegistrationType,
        )}
      </RadioGroupCard>

      <RadioGroupCard
        dataTestId={periodTypeTestId('break')}
        labelTextId="school-timetableStructure-periodType-Break"
        checked={type === PeriodTypeEnum.Break}
        onChange={() => onChange({ type: PeriodTypeEnum.Break, isRegister: false })}
      >
        {description(
          'school-timetableStructure-periodType-Break-Description',
          type === PeriodTypeEnum.Break,
        )}
      </RadioGroupCard>

      <RadioGroupCard
        dataTestId={periodTypeTestId('activity')}
        labelTextId="school-timetableStructure-periodType-Activity"
        checked={type === PeriodTypeEnum.Activity}
        onChange={() => onChange({ type: PeriodTypeEnum.Activity, isRegister: false })}
      >
        {description(
          'school-timetableStructure-periodType-Activity-Description',
          type === PeriodTypeEnum.Activity,
        )}
      </RadioGroupCard>
    </Stack>
  );
};
